import { SwarovskiOptikLocaleFrontend, TypedMoney, type TranslatedValue } from "@swo/storefront";
import { showPrice, type EnrichedSwarovskiProductVariantDiscountedPriceFrontend } from "@swo/shared";
import { mapTranslatedValue, tObj, TWrapper } from "../../../../components/T";
import { hasDiscount } from "../../../products";
import { SwarovskiPriceTag } from "../../../../components/_shared-components/ui-elements/SwarovskiTag";
import { css } from "@emotion/react";
import { SwarovskiInfoTooltip } from "../../../../components/_shared-components/ui-elements/SwarovskiTooltip";
import { CSS_COLORS } from "../../../../styles/styles-css-variables";
import { InterpolateLinearlyReact } from "../../../translations/interpolate-linearly-react";

type DiscountablePrice = {
  startingPrice: TypedMoney;
  discountedStartingPrice: EnrichedSwarovskiProductVariantDiscountedPriceFrontend | undefined;
  lowestPrice30Days: TypedMoney | undefined;
  discountPercentage: number | undefined;
};

// @deprecated - use structured output instead of an already formatted string
// (for reasons of discounted prices)
export function formatStartingPrice(
  price: DiscountablePrice,
  fromPriceLabelTranslation: TranslatedValue,
  lowestPriceLast30DaysTranslation: TranslatedValue,
  localeForNumberFormat: SwarovskiOptikLocaleFrontend,
  avoidDecimalsIfPossible?: boolean,
): React.ReactElement {
  const [first, second, last, lowestPrice, discountPercentage] = formatStartingPriceWithTwoElements(
    price,
    fromPriceLabelTranslation,
    localeForNumberFormat,
    avoidDecimalsIfPossible,
  );

  const priceTagValue = last && (discountPercentage || last);

  const tooltip =
    last && lowestPrice ? (
      <SwarovskiInfoTooltip
        styles={[
          css`
            background-color: ${CSS_COLORS.White};
          `,
        ]}
        content={
          <TWrapper forcedValue={lowestPriceLast30DaysTranslation}>
            <InterpolateLinearlyReact
              translationStringWithInterpolatedValues={lowestPriceLast30DaysTranslation.value}
              args={[lowestPrice]}
            />
          </TWrapper>
        }
      />
    ) : undefined;

  const ret = (
    <div
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <span
        css={css`
          white-space: nowrap;
        `}
      >
        {first} {second}
      </span>
      <div
        css={css`
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          gap: 4px;
        `}
      >
        {priceTagValue && <SwarovskiPriceTag label={priceTagValue}>{priceTagValue}</SwarovskiPriceTag>}
        {tooltip}
      </div>
    </div>
  );

  // Omit information about discount for backwards compatibility
  return ret;
}

export function formatStartingPriceWithTwoElements(
  { startingPrice, discountedStartingPrice, lowestPrice30Days, discountPercentage }: DiscountablePrice,
  fromPriceLabelTranslation: string | TranslatedValue,
  localeForNumberFormat: SwarovskiOptikLocaleFrontend,
  avoidDecimalsIfPossible?: boolean,
): [string, string, string | undefined, string | undefined, string | undefined] {
  const fromPriceLabelTranslationStr =
    typeof fromPriceLabelTranslation === "string" ? fromPriceLabelTranslation : fromPriceLabelTranslation.value;

  const format = (price: TypedMoney) => showPrice(price, localeForNumberFormat, avoidDecimalsIfPossible);

  function hasRealDiscount(): TypedMoney | undefined {
    return discountedStartingPrice &&
      hasDiscount(discountedStartingPrice) &&
      discountedStartingPrice.centAmount !== startingPrice.centAmount
      ? discountedStartingPrice
      : undefined;
  }

  const realDiscount = hasRealDiscount();

  return [
    fromPriceLabelTranslationStr,
    realDiscount ? format(realDiscount) : format(startingPrice),
    realDiscount
      ? `- ${format({ ...startingPrice, centAmount: startingPrice.centAmount - realDiscount.centAmount })}`
      : undefined,
    lowestPrice30Days ? format(lowestPrice30Days) : undefined,
    discountPercentage ? `- ${discountPercentage}%` : undefined,
  ];
}

export function formatOptionalStartingPrice(
  price: DiscountablePrice | undefined,
  fromPriceLabelTranslation: TranslatedValue,
  lowestPriceLast30DaysTranslation: TranslatedValue,
  localeForNumberFormat: SwarovskiOptikLocaleFrontend,
  avoidDecimalsIfPossible?: boolean,
): React.ReactElement | TranslatedValue {
  if (typeof price === "undefined") {
    return mapTranslatedValue(fromPriceLabelTranslation, _ => `_ -`);
  } else {
    return formatStartingPrice(
      price,
      fromPriceLabelTranslation,
      lowestPriceLast30DaysTranslation,
      localeForNumberFormat,
      avoidDecimalsIfPossible,
    );
  }
}
